/* eslint-disable no-multi-spaces */
/**
 * RTP Session class for active WebRTC calls
 */
import debug from 'debug'

const dbg = debug('store:class:rtp_session:')

const OUTGOING = Symbol('call direction outgoing')
const INBOUND  = Symbol('call direction inbound')

// call states
const EARLY    = Symbol('call initiating state')
const ANSWERED = Symbol('call answered state')

/**
 * RTP Sessions class for SIPUA
 *
 * @param {Object} with keys callid, name, number, outgoing
 */
export default class {
  constructor ({ callid, name, number, outgoing, xfer }) {
    this.callid     = callid
    this.dir        = outgoing ? OUTGOING : INBOUND
    this.state      = EARLY
    this.mute       = false
    this.onhold     = false
    this.started    = Date.now()
    this.answered   = null // answer time will be set when confirmed
    this.recording  = false
    this.monfile    = ''
    this.monCDRpath = ''
    this.moncmd     = ''
    this.number     = number
    this.name       = name
    if (xfer?.callid) {
      this.xfer = xfer
    }
    this.bargein = {}
    dbg(`New RTPSession class instance created with id: ${callid}`)
  }

  /**
   * @static
   * Find session by call-id and delete from sessions array
   *
   * @param {Array} State sessions
   * @param {String} Call-Id to search
   * @return Boolean
   */
  static delete (sessions, callid) {
    const idx = sessions.findIndex(s => s.callid === callid)
    if (idx === -1) {
      dbg(`Failed to find call-id ${callid}. Can not delete session. Maybe not added.`)
      return false
    }
    sessions.splice(idx, 1)
    return true
  }

  /**
   * @static
   * Set session as answered by call-id
   *
   * @param {Array} State sessions
   * @param {String} Call-Id to search
   * @return Boolean
   */
  static setAnswered (sessions, callid) {
    const idx = sessions.findIndex(s => s.callid === callid)
    if (idx === -1) {
      dbg(`ERROR: Failed to find call-id ${callid}. Can not set session on call state.`)
      return false
    }
    sessions[idx].state = ANSWERED
    sessions[idx].answered = Date.now()
    return true
  }

  /**
   * @static
   * Hold/Unhold session.
   *
   * @param {Array} sessions list
   * @param {String} session to set hold/unhold
   * @param {Boolean} true for hold, false for unhold
   */
  static hold (sessions, callid, enable) {
    const session = sessions.find(s => s.callid === callid)
    if (!session) return false
    sessions.forEach(s => s.setOnHold(true))
    session.setOnHold(enable)
    return true
  }

  /**
   * @static
   * Mute/Unmute session
   *
   * @param {Array} sessions list
   * @param {String} session to set mute/unmute
   * @param {Boolean}
   */
  static mute (sessions, callid, enable) {
    const session = sessions.find(s => s.callid === callid)
    if (!session) return false
    session.setMute(enable)
    return true
  }

  /**
   * @static
   * Update session by call-id and set call as being recorded/monitored
   *
   * @param {Array} sessions list
   * @param {Object} AMI UserEvent CallMonitor
   * @param {Boolean}
   */
  static setRecording (sessions, evt) {
    if (!evt.chanvariable || !evt.chanvariable.SIPCALLID) {
      return false
    }

    const callid = evt.chanvariable.SIPCALLID
    const session = sessions.find(s => [callid, evt.sipinitclid].includes(s.callid))
    if (!session) return false

    session.channel = evt.channel
    session.amisrvid = evt.amisrvid
    session.recording = evt.recording === 'on'
    session.monfile   = evt.monfile
    session.monCDRpath = evt.moncdrpath
    session.moncmd    = evt.moncmd
  }

  /**
   * @static
   * Setup barge-in (call spy) session
   * @param {Array} sessions list
   * @param {String} callid
   * @param {String} num barge-in caller number
   * @param {String} legnum other leg barge-in number
   * @param {String} mode barge-in
   */
  static setBargein (sessions, callid, num, legnum, mode) {
    const session = sessions.find(s => callid === s.callid)
    if (!session) return false
    session.bargein = { mode, num, legnum }
  }

  static toggleRec (sessions, callid, enabled) {
    const session = sessions.find(s => callid === s.callid)
    if (!session) return false
    session.recording = enabled
  }

  /**
   * @static
   * Update session by call-id and set Asterisk channel id
   *
   * @param {Array} sessions list
   * @param {Object} AMI Event BridgeEnter
   * @param {Boolean}
   */
  static setChanID (sessions, evt) {
    if (!evt.chanvariable || !evt.chanvariable.SIPCALLID) {
      return false
    }

    const callid = evt.chanvariable.SIPCALLID
    const session = sessions.find(s => s.callid === callid)
    if (!session) return false

    session.channel = evt.channel
    session.amisrvid = evt.amisrvid
    session.bridgeid = evt.bridgeuniqueid

    return true
  }

  /**
   * @static
   * Update session by call-id and set Asterisk bridge peer channel id
   *
   * @param {Array} sessions list
   * @param {Object} AMI Event BridgeInfoChannel
   * @param {Boolean}
   */
  static setBridgePeerChan (sessions, evt) {
    if (!evt.chanvariable || !evt.chanvariable.SIPCALLID) {
      return false
    }

    const callid = evt.chanvariable.SIPCALLID
    const session = sessions.find(s => s.callid === callid)
    if (!session) return false

    if (evt.chanvariable.BRIDGEPEER) {
      session.bridgePeer = evt.chanvariable.BRIDGEPEER
    }
  }

  /**
   * @static
   * Update RTCPeerConnection session QoS (DSCP) flags with higher value
   *
   * @param {Object} sess RTCPeerConnection of current WebRTC session
   */
  static setQoS (sess) {
    if (!sess) {
      return
    }

    const senders = sess.getSenders()

    senders.forEach(sender => {
      const params = sender.getParameters()
      params.encodings.forEach(p => {
        p.priority = 'high'
        p.networkPriority = 'high'
      })
      params.priority = 'high'
      sender.setParameters(params)
    })
  }

  /**
   * @getters
   */
  get isOutgoing () { return this.dir === OUTGOING }

  get isInbound () { return this.dir === INBOUND }

  get isOnHold () { return this.onhold }

  get isMuted () { return this.mute }

  // on call
  get isOnCall () {
    return !this.isOnHold && !this.isMuted && this.state === ANSWERED
  }

  // new incoming call
  get isReceiving () {
    return this.isInbound && this.state === EARLY
  }

  // initiated outgoing call but not answered yet. Ringing
  get isSending () {
    return this.isOutgoing && this.state === EARLY
  }

  get isBargein () {
    return !!this.bargein?.mode && !!this.bargein?.num
  }

  setOnHold (enabled) {
    this.onhold = !!enabled
  }

  setMute (enabled) {
    this.mute = !!enabled
  }
}
